import {
  CurrencyPipe,
  DatePipe,
  DecimalPipe,
  PercentPipe,
} from '@angular/common'
import { Pipe, type PipeTransform, inject } from '@angular/core'
import * as ents from '@attain-apps/entities'
import { TypeChecker } from '@attain-apps/type-guards'
import { format } from 'pretty-format'

import * as models from './models'

// TODO: convert to standalone pipe
@Pipe({
  name: 'atnDsFormattedValue',
  standalone: false,
})
export class AtnDsFormattedValuePipe implements PipeTransform {
  private readonly _decimalPipe: DecimalPipe = inject(DecimalPipe)
  private readonly _percentPipe: PercentPipe = inject(PercentPipe)
  private readonly _datePipe: DatePipe = inject(DatePipe)
  private readonly _currencyPipe: CurrencyPipe = inject(CurrencyPipe)

  public transform(
    value: ents.core.common.value.Raw | undefined,
    type: ents.core.common.value.types.Type = ents.core.common.value.types
      .Intrinsic.Text,
    options?: models.Options,
  ): string {
    if (value === undefined) {
      return ''
    }
    const errorMessage = `Value ${format(value)} is of invalid datatype for type ${format(type)}`
    let valueFormat: string | undefined
    switch (type) {
      case ents.core.common.value.types.Intrinsic.Integer:
      case ents.core.common.value.types.Intrinsic.Decimal:
      case ents.core.common.value.types.Intrinsic.Percent:
      case ents.core.common.value.types.Intrinsic.Currency: {
        if (!TypeChecker.isString(value) && !TypeChecker.isNumber(value)) {
          throw new Error(errorMessage)
        }
        if (models.Options.Numerical.isNumerical(options)) {
          valueFormat = this._getNumericalFormat(options)
        }
        if (type === ents.core.common.value.types.Intrinsic.Percent) {
          return `${this._percentPipe.transform(value, valueFormat)}`
        } else if (type === ents.core.common.value.types.Intrinsic.Currency) {
          return (
            this._currencyPipe.transform(value, 'USD', 'symbol', valueFormat) ??
            ''
          )
        }
        return this._decimalPipe.transform(value, valueFormat) ?? ''
      }
      case ents.core.common.value.types.Intrinsic.Date: {
        if (
          !TypeChecker.isDate(value) &&
          !TypeChecker.isString(value) &&
          !TypeChecker.isNumber(value)
        ) {
          throw new Error(errorMessage)
        }
        if (models.Options.Date.isDate(options)) {
          return this._datePipe.transform(value, options) ?? ''
        }
        return ents.core.display.date.format(value) ?? ''
      }
      case ents.core.common.value.types.Intrinsic.Boolean: {
        if (!TypeChecker.isBoolean(value)) {
          throw new Error(errorMessage)
        }
        value = value.toString()
        return value.charAt(0).toUpperCase() + value.slice(1)
      }
      case ents.core.common.value.types.Intrinsic.Object:
      case ents.core.common.value.types.Intrinsic.List: {
        if (!TypeChecker.isObject(value) && !TypeChecker.isArray(value)) {
          throw new Error(errorMessage)
        }
        return JSON.stringify(value)
      }
      default:
        // If type is not specified (defaults to 'Text') we want to check if
        // values is of type object/array so that we can format it properly
        if (TypeChecker.isObject(value) || TypeChecker.isArray(value)) {
          return JSON.stringify(value)
        }
        // NOTE: call to string, and consider it to be correct option, this is
        // better then throwing an error prematurely
        return value?.toString()
    }
  }

  private _getNumericalFormat(options: models.Options.Numerical): string {
    if (TypeChecker.isNumber(options)) {
      return `1.${options}-${options}`
    } else {
      return `1.${options.min ?? 0}-${options.max ?? options.min}`
    }
  }
}
